import { useEffect, useRef } from 'react';
import { getTour } from './registry';
import { useTour } from './TourContext';

/**
 * Auto-start a page's tour the first time a user lands on it.
 *
 * It only fires when the tour has never been started (status 'not_started') and
 * fires at most once per mount. If the user has already started, paused,
 * completed it, another tour is running, or the tour is missing / coming soon /
 * empty, it does nothing — so it never nags on repeat visits.
 *
 * The decision is deferred by a short delay because tour progress is loaded
 * asynchronously (from localStorage) and the page's target elements need to
 * exist before the overlay anchors to them. `getStatus` is a dependency, so the
 * effect re-evaluates once progress has loaded and the status has settled.
 */
export function useAutoStartTour(tourId: string, opts?: { delayMs?: number }): void {
  const { startTour, getStatus, isRunning } = useTour();
  const firedRef = useRef(false);
  const delayMs = opts?.delayMs ?? 800;

  useEffect(() => {
    if (firedRef.current) return;

    const tour = getTour(tourId);
    if (!tour || tour.comingSoon || tour.steps.length === 0) return;

    // Another tour is active, or this one was already started/paused/completed
    // (once progress has loaded) — don't hijack or restart it.
    if (isRunning) return;
    if (getStatus(tourId) !== 'not_started') {
      firedRef.current = true;
      return;
    }

    const timer = window.setTimeout(() => {
      // Re-check at fire time: by now progress is loaded and status is accurate.
      if (!firedRef.current && !isRunning && getStatus(tourId) === 'not_started') {
        firedRef.current = true;
        startTour(tourId);
      }
    }, delayMs);

    return () => window.clearTimeout(timer);
  }, [tourId, getStatus, isRunning, startTour, delayMs]);
}
